import React, { useEffect, useState, useCallback } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import { authAPI } from '../services/api';
import Toast from '../components/Toast';
import { utcTimeToLocal, localTimeToUTC } from '../utils/timezone';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface TimeRange {
  start: string;
  end: string;
}

interface DayWorkingHours {
  weekday: number;
  is_active: boolean;
  ranges: TimeRange[];
}

// Helper function to round time to nearest 30-minute interval
const roundToNearest30Minutes = (time: string): string => {
  if (!time || !time.includes(':')) return '09:00';
  const [hours, minutes] = time.split(':').map(Number);
  const roundedMinutes = Math.round(minutes / 30) * 30;
  if (roundedMinutes >= 60) {
    return `${(hours + 1).toString().padStart(2, '0')}:00`;
  }
  return `${hours.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`;
};

// Custom Time Picker Component with 30-minute intervals
interface TimePickerProps {
  value: string; // Format: "HH:MM"
  onChange: (value: string) => void;
  className?: string;
}

const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, className = '' }) => {
  // Generate time options with 30-minute intervals (00:00 to 23:30)
  const timeOptions: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeOptions.push(timeString);
    }
  }

  // Format time for display (e.g., "09:00" -> "9:00 AM")
  const formatTimeForDisplay = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Ensure value is rounded to 30 minutes
  const roundedValue = value ? roundToNearest30Minutes(value) : '09:00';

  return (
    <select
      value={roundedValue}
      onChange={(e) => onChange(e.target.value)}
      className={`px-3 py-2 text-xs font-medium border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white cursor-pointer shadow-sm hover:border-primary-400 transition-colors ${className}`}
    >
      {timeOptions.map((time) => (
        <option key={time} value={time}>
          {formatTimeForDisplay(time)}
        </option>
      ))}
    </select>
  );
};

// Day Working Hours Editor Component (reused from UsersPageV2)
interface DayWorkingHoursEditorProps {
  dayName: string;
  weekday: number;
  isActive: boolean;
  ranges: TimeRange[];
  onToggle: () => void;
  onRangesChange: (ranges: TimeRange[]) => void;
}

const DayWorkingHoursEditor: React.FC<DayWorkingHoursEditorProps> = ({
  dayName,
  isActive,
  ranges,
  onToggle,
  onRangesChange,
}) => {
  const addRange = () => {
    onRangesChange([...ranges, { start: '09:00', end: '17:00' }]);
  };

  const removeRange = (index: number) => {
    onRangesChange(ranges.filter((_, i) => i !== index));
  };

  const updateRange = (index: number, field: 'start' | 'end', value: string) => {
    const newRanges = [...ranges];
    newRanges[index][field] = value;
    onRangesChange(newRanges);
  };

  return (
    <div className={`border-2 rounded-lg p-3 ${isActive ? 'border-primary-300 bg-primary-50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-center justify-between mb-2">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={isActive}
            onChange={onToggle}
            className="mr-2 h-4 w-4 text-primary-600 rounded border-gray-300"
          />
          <span className={`font-semibold text-sm ${isActive ? 'text-primary-900' : 'text-gray-500'}`}>
            {dayName}
          </span>
        </label>
      </div>

      {isActive && (
        <div className="space-y-2">
          {ranges.map((range, index) => (
            <div key={index} className="flex items-center space-x-2">
              <TimePicker
                value={range.start}
                onChange={(value) => updateRange(index, 'start', value)}
                className="flex-1 min-w-0"
              />
              <span className="text-gray-500 text-xs font-medium">-</span>
              <TimePicker
                value={range.end}
                onChange={(value) => updateRange(index, 'end', value)}
                className="flex-1 min-w-0"
              />
              {ranges.length > 1 && (
                <button
                  onClick={() => removeRange(index)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                  title="Remove range"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addRange}
            className="w-full px-3 py-2 text-xs text-primary-700 bg-primary-100 hover:bg-primary-200 rounded-lg transition font-medium"
          >
            + Add Time Range
          </button>
        </div>
      )}
    </div>
  );
};

const HoursPage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calendarConnecting, setCalendarConnecting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [workingHours, setWorkingHours] = useState<DayWorkingHours[]>([]);
  const [slackUserId, setSlackUserId] = useState('');
  const [savingSlackUserId, setSavingSlackUserId] = useState(false);

  const handleCalendarConnect = useGoogleLogin({
    flow: 'auth-code',
    scope: [
      'https://www.googleapis.com/auth/calendar',
      'https://www.googleapis.com/auth/calendar.events',
    ].join(' '),
    onSuccess: async (codeResponse) => {
      setCalendarConnecting(true);
      try {
        await authAPI.connectGoogleCalendar(codeResponse.code);
        await refreshUser();
        setToast({ message: '✓ Google Calendar connected successfully!', type: 'success' });
      } catch (err: any) {
        const msg = err.response?.data?.error || 'Failed to connect Google Calendar';
        setToast({ message: `✗ ${msg}`, type: 'error' });
      } finally {
        setCalendarConnecting(false);
      }
    },
    onError: (error) => {
      setToast({ message: `✗ Google OAuth error: ${error.error || 'Unknown error'}`, type: 'error' });
    },
  });

  const loadWorkingHours = useCallback(() => {
    if (!user) return;

    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const defaultHours: DayWorkingHours[] = dayNames.map((name, idx) => ({
      weekday: idx,
      is_active: false,
      ranges: []
    }));

    // Load from user.working_hours if available
    // Convert from UTC (stored in DB) to local timezone for display
    if (user.working_hours && Array.isArray(user.working_hours)) {
      user.working_hours.forEach((dayData: any) => {
        const dayIndex = dayNames.indexOf(dayData.day);
        if (dayIndex >= 0) {
          defaultHours[dayIndex] = {
            weekday: dayIndex,
            is_active: dayData.is_active || false,
            ranges: dayData.time_ranges?.map((r: any) => {
              // Convert UTC time from DB to local timezone for display
              const localStart = utcTimeToLocal(r.start);
              const localEnd = utcTimeToLocal(r.end);
              return { 
                start: roundToNearest30Minutes(localStart), 
                end: roundToNearest30Minutes(localEnd) 
              };
            }) || []
          };
        }
      });
    }

    setWorkingHours(defaultHours);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (user) {
      loadWorkingHours();
      setSlackUserId(user.slack_user_id || '');
    }
  }, [user, loadWorkingHours]);

  const saveSlackUserId = async () => {
    if (user?.role === 'manager' && !slackUserId.trim()) {
      setToast({ message: '⚠ Slack User ID is required for managers', type: 'warning' });
      return;
    }
    setSavingSlackUserId(true);
    try {
      await authAPI.updateSlackUserId(slackUserId.trim());
      await refreshUser();
      setToast({ message: '✓ Slack ID saved successfully!', type: 'success' });
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.message;
      setToast({ message: `✗ Failed to save: ${msg}`, type: 'error' });
    } finally {
      setSavingSlackUserId(false);
    }
  };

  const handleDayToggle = (dayIndex: number) => {
    setWorkingHours(prev => {
      const newHours = [...prev];
      newHours[dayIndex] = {
        ...newHours[dayIndex],
        is_active: !newHours[dayIndex].is_active
      };
      return newHours;
    });
  };

  const handleRangesChange = (dayIndex: number, newRanges: TimeRange[]) => {
    setWorkingHours(prev => {
      const newHours = [...prev];
      newHours[dayIndex] = {
        ...newHours[dayIndex],
        ranges: newRanges
      };
      return newHours;
    });
  };

  const saveWorkingHours = async () => {
    if (!user) return;

    setSaving(true);
    try {
      // Prepare working hours in API format
      // Convert from local timezone (user input) to UTC for storage in DB
      const apiWorkingHours = workingHours.map((day) => ({
        weekday: day.weekday,
        isActive: day.is_active,
        ranges: day.ranges.map(r => ({ 
          start: localTimeToUTC(r.start),  // Convert local time to UTC
          end: localTimeToUTC(r.end)        // Convert local time to UTC
        }))
      }));

      const updatedUser = await authAPI.updateWorkingHours(apiWorkingHours);
      
      setToast({ message: '✓ Working hours saved successfully!', type: 'success' });
      
      // Reload working hours from updated user data
      const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const defaultHours: DayWorkingHours[] = dayNames.map((name, idx) => ({
        weekday: idx,
        is_active: false,
        ranges: []
      }));

      if (updatedUser.working_hours && Array.isArray(updatedUser.working_hours)) {
        updatedUser.working_hours.forEach((dayData: any) => {
          const dayIndex = dayNames.indexOf(dayData.day);
          if (dayIndex >= 0) {
            defaultHours[dayIndex] = {
              weekday: dayIndex,
              is_active: dayData.is_active || false,
              ranges: dayData.time_ranges?.map((r: any) => {
                // Convert UTC time from DB to local timezone for display
                const localStart = utcTimeToLocal(r.start);
                const localEnd = utcTimeToLocal(r.end);
                return { 
                  start: roundToNearest30Minutes(localStart), 
                  end: roundToNearest30Minutes(localEnd) 
                };
              }) || []
            };
          }
        });
      }

      setWorkingHours(defaultHours);
    } catch (err: any) {
      console.error('Save working hours error:', err);
      setToast({ 
        message: `✗ Failed to save: ${err.response?.data?.detail || err.response?.data?.error || err.message}`, 
        type: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  // Check if user is a manager
  if (user && user.role !== 'manager') {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
              <h1 className="text-2xl font-bold text-red-900 mb-4">⛔ Access Denied</h1>
              <p className="text-red-800">Only Managers can access this page.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
            <p className="mt-4 text-gray-600">Loading working hours...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">⏰ Working Hours</h1>
            <p className="mt-2 text-gray-600">
              Configure your weekly availability by setting working hours for each day
            </p>
          </div>

          {/* Google Calendar Connection */}
          <div className={`mb-6 rounded-xl border-2 p-6 shadow-sm ${user?.has_calendar_access ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-1">Google Calendar</h2>
                {user?.has_calendar_access ? (
                  <p className="text-sm text-green-700">Connected — booking events will sync automatically.</p>
                ) : (
                  <p className="text-sm text-yellow-800">
                    Not connected. Connect your Google Calendar so bookings create Meet events and sync your schedule.
                  </p>
                )}
              </div>
              <button
                onClick={() => handleCalendarConnect()}
                disabled={calendarConnecting}
                className={`ml-4 flex-shrink-0 flex items-center px-4 py-2 rounded-lg font-semibold text-sm transition disabled:opacity-50 disabled:cursor-not-allowed ${
                  user?.has_calendar_access
                    ? 'bg-green-100 text-green-800 hover:bg-green-200 border border-green-300'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-300 hover:border-accent-500 shadow'
                }`}
              >
                {calendarConnecting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Connecting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    {user?.has_calendar_access ? 'Reconnect' : 'Connect Google Calendar'}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Slack Notifications */}
          <div className="mb-6 rounded-xl border-2 border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-1">
              Slack {user?.role === 'manager' && <span className="text-red-500">*</span>}
            </h2>
            <p className="text-sm text-gray-600 mb-3">
              Add your Slack member ID so lead notifications tag you directly in Slack instead of showing your email.
            </p>
            <details className="mb-4 text-sm text-gray-600">
              <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">
                How do I find my Slack member ID?
              </summary>
              <p className="mt-2">
                In Slack, click your profile photo → <span className="font-medium">View profile</span> → click the{' '}
                <span className="font-medium">⋮ (more)</span> icon → <span className="font-medium">Copy member ID</span>.
                It looks like <code className="bg-gray-100 px-1 rounded">U0123456789</code>.
              </p>
            </details>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={slackUserId}
                onChange={(e) => setSlackUserId(e.target.value)}
                placeholder="U0123456789"
                required={user?.role === 'manager'}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                onClick={saveSlackUserId}
                disabled={savingSlackUserId}
                className="flex-shrink-0 px-4 py-2 rounded-lg font-semibold text-sm bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-300 hover:border-accent-500 shadow transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingSlackUserId ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Working Hours Configuration */}
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-sm">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Weekly Schedule (TIMEZONE: UTC)</h2>
              <p className="text-sm text-gray-600">
                Select the days you work and set your available time ranges. You can add multiple time ranges per day.
              </p>
            </div>

            <div className="space-y-3">
              {/* First Row: Mon-Thu (4 columns) */}
              <div className="grid grid-cols-4 gap-2">
                {workingHours.slice(0, 4).map((dayData, dayIndex) => {
                  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday'];
                  return (
                    <DayWorkingHoursEditor
                      key={dayIndex}
                      dayName={dayNames[dayIndex]}
                      weekday={dayIndex}
                      isActive={dayData.is_active}
                      ranges={dayData.ranges}
                      onToggle={() => handleDayToggle(dayIndex)}
                      onRangesChange={(newRanges: TimeRange[]) => handleRangesChange(dayIndex, newRanges)}
                    />
                  );
                })}
              </div>
              
              {/* Second Row: Fri-Sun (3 columns) */}
              <div className="grid grid-cols-3 gap-2">
                {workingHours.slice(4, 7).map((dayData, idx) => {
                  const dayIndex = idx + 4;
                  const dayNames = ['Friday', 'Saturday', 'Sunday'];
                  return (
                    <DayWorkingHoursEditor
                      key={dayIndex}
                      dayName={dayNames[idx]}
                      weekday={dayIndex}
                      isActive={dayData.is_active}
                      ranges={dayData.ranges}
                      onToggle={() => handleDayToggle(dayIndex)}
                      onRangesChange={(newRanges: TimeRange[]) => handleRangesChange(dayIndex, newRanges)}
                    />
                  );
                })}
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-6 flex items-center justify-end space-x-3">
              <button
                onClick={saveWorkingHours}
                disabled={saving}
                className={`px-6 py-3 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition font-semibold shadow ${
                  saving ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {saving ? (
                  <span className="flex items-center">
                    <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </span>
                ) : (
                  '💾 Save Working Hours'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default HoursPage;
